import { useMemo } from 'react';
import { Button, Grid, GridItem, Skeleton, Text, useDisclosure, useConst } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useQueryParam } from 'use-query-params';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';

import { useUserStore } from '@store/app.store';
import { ReskinMeetingCardContext } from '@components/general/card/reskin-meeting-card/reskin-meeting-card.component';
import ReskinMeetingMenu from '@components/general/card/reskin-meeting-card/reskin-meeting-menu.component';
import { StatusTimeLabel } from '@components/general/card/status-time-label.component';
import LabelsModal from '@components/general/modals/labels-modal.component';
import AddForaModal from '@components/meeting/add-fora-modal.component';
import { useAllMeetingContentById } from '@services/query-services/meetings.query-service';
import { HorizontalDotMenuIcon } from '@assets/icons';
import {
  getMeetingBackgroundColor,
  getMeetingActionButton,
  multiBotStatusChangeUpdaterFn,
  singleBotStatusChangeUpdaterFn
} from '@util/helpers';
import {
  useControlBarState,
  useMeetingBotStatuses,
  useMeetingManagementActions,
  useMeetingTimeStatus,
  useUpdateBotFromPusherEvent,
  usePusherChannel,
  usePusherEvent
} from '@util/hooks';

/**
 * @param {{
 *   meeting: Meeting,
 *   omitBorderTop: boolean,
 *   onMeetingActionClick: Function,
 *   onCardActionClick: Function
 * }} props
 */
export default function MeetingRow({
  meeting,
  omitBorderTop,
  onMeetingActionClick,
  onCardActionClick
}) {
  const { data: allMeetingContent, isLoading } = useAllMeetingContentById(meeting?.id, false, {
    enabled: !!meeting?.id
  });

  const queryClient = useQueryClient();
  const user = useUserStore((state) => state.user);
  const date = useQueryParam('date');
  const today = useConst(new Date());
  const navigate = useNavigate();
  const bot = allMeetingContent?.bot;
  const card = allMeetingContent?.card;
  const stateForMeetingUI = useControlBarState(meeting, bot);
  const { wasSummarized, hasTranscripts } = useMeetingBotStatuses(bot);
  const {
    startMeetingAndRecording,
    showAddForaButton,
    showAddForaBackButton,
    addForaModalDisclosure
  } = useMeetingManagementActions(meeting, bot);
  const meetingTimeStatus = useMeetingTimeStatus(meeting, 60 * 1000);

  const bgColor = getMeetingBackgroundColor(meetingTimeStatus);
  const actionButton = getMeetingActionButton({
    stateForMeetingUI,
    meetingTimeStatus,
    startMeetingAndRecording,
    showAddForaButton,
    showAddForaBackButton,
    addForaModalDisclosure,
    wasSummarized,
    hasTranscripts
  });

  const labelsModalDisclosure = useDisclosure();
  const contextValue = useMemo(
    () => ({
      meeting,
      bot,
      card,
      onMeetingActionClick,
      onCardActionClick,
      labelsModalDisclosure,
      showShareInMenu: true
    }),
    [meeting, bot, card, onMeetingActionClick, onCardActionClick, labelsModalDisclosure, date]
  );

  // https://foraday.atlassian.net/browse/FORA-1975
  // Update bots on the agenda page but only for "today", as
  // we wouldn't typically expect events coming from bots on other days
  useUpdateBotFromPusherEvent(
    bot?.id,
    'status_changes',
    ['meetings', 'agenda', format(today, 'M/dd/yyyy')],
    multiBotStatusChangeUpdaterFn
  );

  // https://foraday.atlassian.net/browse/FORA-1975
  // Also update the results from the useAllMeetingContentById query
  useUpdateBotFromPusherEvent(
    bot?.id,
    'status_changes',
    ['meetings', { id: meeting?.id }, 'all'],
    singleBotStatusChangeUpdaterFn
  );

  // https://foraday.atlassian.net/browse/FORA-1975
  // the agenda.page handles the agenda query when a new card comes in, but
  // we need to handle the useAllMeetingContentById query here, as it's
  // specific to a meeting
  const { channel: userIdChannel } = usePusherChannel(user?.id);
  usePusherEvent(userIdChannel, 'on_new_card', () => {
    queryClient.invalidateQueries(['meetings', { id: meeting?.id }, 'all']);
  });

  return (
    <Grid
      gap={2}
      p={3}
      w='full'
      bgColor={bgColor}
      templateColumns={{ base: '100px 1fr auto auto', md: '150px 1fr auto auto' }}
      gridTemplateRows={card?.headline ? '1fr auto' : '1fr'}
      cursor='pointer'
      alignItems='center'
      borderTop={omitBorderTop ? 'none' : 'reskin'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/meeting/${meeting?.id}`);
      }}
      data-testid='meeting-row-grid'
    >
      <GridItem>
        {isLoading ? (
          <Skeleton
            h={4}
            w={20}
          />
        ) : (
          <StatusTimeLabel
            p={0}
            meeting={meeting}
            bot={meeting?.bot}
          />
        )}
      </GridItem>

      <GridItem
        colSpan={{ base: 4, md: 1 }}
        rowStart={{ base: 2, md: 1 }}
        colStart={{ base: 1, md: 2 }}
        layerStyle={{ md: 'ellipsis-text' }}
      >
        {isLoading ? (
          <Skeleton
            h={5}
            w={80}
          />
        ) : (
          <Text
            variant='body-reg-lg'
            fontWeight={450}
            flex={1}
            fontSize='16px'
            layerStyle={{ md: 'ellipsis-text' }}
            minW={12}
          >
            {meeting?.title}
          </Text>
        )}
      </GridItem>
      <GridItem colStart={3}>
        {isLoading ? (
          <Skeleton
            h={2}
            w={16}
          />
        ) : (
          <>
            {actionButton && (
              <Button
                pointerEvents={actionButton?.propagate && 'none'}
                variant='link'
                color={actionButton?.color}
                onClick={(e) => {
                  if (!actionButton?.propagate) {
                    e.stopPropagation();
                  }
                  actionButton?.onClick?.();
                }}
              >
                <Text variant='label-reg-md'>{actionButton.text}</Text>
              </Button>
            )}
          </>
        )}
      </GridItem>
      <GridItem>
        {isLoading ? (
          <Skeleton boxSize={5} />
        ) : (
          <ReskinMeetingCardContext.Provider value={contextValue}>
            <ReskinMeetingMenu
              buttonProps={{
                variant: 'reskinGhost',
                colorScheme: 'surface',
                size: 'xs',
                icon: <HorizontalDotMenuIcon color='surface.onSurfaceVariant' />
              }}
              showViewTranscriptButton
            />
          </ReskinMeetingCardContext.Provider>
        )}
      </GridItem>

      {card?.headline && (
        <GridItem
          colStart={{ base: 1, md: 2 }}
          colSpan={{ base: 4, md: 3 }}
          rowStart={{ base: 3, md: 2 }}
        >
          <Text
            variant='label-600-lg'
            color='surface.onSurfaceVariant'
            noOfLines={3}
          >
            {card?.headline}
          </Text>
        </GridItem>
      )}

      <LabelsModal
        meeting={meeting}
        card={card}
        disclosure={labelsModalDisclosure}
      />
      <AddForaModal
        meeting={meeting}
        disclosure={addForaModalDisclosure}
      />
    </Grid>
  );
}
